import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader, X, Search, Upload, Mail, Download, RotateCcw, CheckCircle, XCircle, SlidersHorizontal, Check, ChevronDown, ChevronLeft, ChevronRight, FolderOpen, ArrowLeft, AlertTriangle, Info, MessageSquarePlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { getErrorMessage } from "../lib/api";
import { fmt } from "../lib/utils";
import Avatar from "./common/Avatar";
import StatusDot from "./common/StatusDot";
import Sheet from "./common/Sheet";
import Button from "./common/Button";
import SectionLabel from "./common/SectionLabel";

type Tab = "analyse" | "kontrakt" | "pdf";

// FILTERS defined inside component to use t()

// E1: «grønn» (ren linje) — speiler backend arithmetic_validator.is_green. Kun grønne
// linjer godkjennes med ett klikk; flaggede krever begrunnelse.
const GREEN_LINE_STATUSES = ["OK", "GODKJENT_CO"];
const isLineGreen = (linje: any) => GREEN_LINE_STATUSES.includes(linje?.status);

export default function HistoryView() {
  const { t, i18n } = useTranslation();
  const FILTERS: { value: string; label: string }[] = [
    { value: "", label: t("invoice.alle") },
    { value: "flagged", label: t("invoice.flagget") },
    { value: "approved", label: t("common.godkjent") },
    { value: "delvis_godkjent", label: t("invoice.delvis") },
    { value: "godkjent", label: t("invoice.klar_betaling") },
    { value: "betalt", label: t("invoice.betalt") },
  ];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlProject = searchParams.get("project");
  const urlInvoice = searchParams.get("invoice");
  const urlLine = searchParams.get("line");

  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(urlProject);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(urlInvoice);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [activeTab, setActiveTab] = useState<Tab>("analyse");
  const [highlightLine, setHighlightLine] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [contractProgress, setContractProgress] = useState<any>(null);
  const [progressLoading, setProgressLoading] = useState(false);

  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadContractId, setUploadContractId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const [draftEmail, setDraftEmail] = useState<{ draft: string; to: string; subject: string; invoice_id?: string } | null>(null);
  const [draftLang, setDraftLang] = useState<string>("nb");
  const [draftLoading, setDraftLoading] = useState(false);
  const [lineActionLoading, setLineActionLoading] = useState<string | null>(null);
  const [showRejectForm, setShowRejectForm] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState<Record<string, string>>({});
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [replacementInfo, setReplacementInfo] = useState<any>(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [linkCandidates, setLinkCandidates] = useState<any[]>([]);
  const [erpQueue, setErpQueue] = useState<any[]>([]);
  const [showQueue, setShowQueue] = useState(false);
  const [queueSel, setQueueSel] = useState<Record<string, { project_id: string; contract_id: string }>>({});
  const [assigning, setAssigning] = useState<string | null>(null);

  // ── API functions ──
  const fetchProjects = async () => {
    try {
      const res = await api.get("/projects/");
      const p = res.data.projects;
      setProjects(p);
      const initial = urlProject || (p.length > 0 ? p[0].id : null);
      if (initial) { setSelectedProjectId(initial); fetchInvoices(initial); }
    } catch {}
  };

  const fetchInvoices = async (projectId?: string) => {
    const pid = projectId || selectedProjectId;
    if (!pid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      params.set("page", "1");
      params.set("per_page", "50");
      const res = await api.get(`/portfolio/project/${pid}/invoices?${params}`);
      setInvoices(res.data.invoices);
      // Auto-select første faktura
      if (res.data.invoices.length > 0 && !selectedInvoiceId) {
        setSelectedInvoiceId(res.data.invoices[0].id);
      }
    } catch {}
    finally { setLoading(false); }
  };

  const fetchQueue = async () => {
    try { const res = await api.get("/erp/queue"); setErpQueue(res.data.queue || []); } catch {}
  };

  // A1: korrigert-faktura-lenking
  const fetchReplacement = async (invId: string) => {
    try { const r = await api.get(`/portfolio/invoices/${invId}/replacement`); setReplacementInfo(r.data); }
    catch { setReplacementInfo(null); }
  };
  const replAction = async (path: string, body?: any) => {
    if (!selectedInvoice) return;
    try {
      await api.post(`/portfolio/invoices/${selectedInvoice.id}/${path}`, body || {});
      setShowLinkPicker(false);
      await fetchInvoices();
      await fetchReplacement(selectedInvoice.id);
    } catch (e: any) { setUploadMessage({ text: getErrorMessage(e), ok: false }); }
  };
  const openLinkPicker = async () => {
    if (!selectedInvoice) return;
    try { const r = await api.get(`/portfolio/invoices/${selectedInvoice.id}/replacement-candidates`); setLinkCandidates(r.data.candidates || []); setShowLinkPicker(true); }
    catch (e: any) { setUploadMessage({ text: getErrorMessage(e), ok: false }); }
  };

  const assignContract = async (item: any) => {
    const sel = queueSel[item.id];
    if (!sel?.contract_id) return;
    await doAssign(item, sel.contract_id, "manual");
  };

  // 8c: koble via et rangert FORSLAG (ett klikk). kilde = "suggested" hvis brukeren
  // valgte den fremhevede kandidaten, ellers "manual" — logges i revisjonssporet.
  const assignCandidate = async (item: any, contractId: string) => {
    const kilde = contractId === item.suggested_contract_id ? "suggested" : "manual";
    await doAssign(item, contractId, kilde);
  };

  const doAssign = async (item: any, contractId: string, source: string) => {
    setAssigning(item.id);
    try {
      await api.post(`/erp/invoices/${item.id}/assign-contract`, { contract_id: contractId, source });
      setErpQueue(prev => prev.filter(q => q.id !== item.id));
      setUploadMessage({ text: t("invoice.koble_ok"), ok: true });
      fetchInvoices();
    } catch (e: any) {
      setUploadMessage({ text: getErrorMessage(e, t("invoice.koble_feil")), ok: false });
    } finally { setAssigning(null); }
  };

  // 8c: «Vis kontrakt» — åpner kontraktens PDF ved siden av (sidepanel-erstatning).
  const viewContractPdf = (cand: any) => {
    if (!cand?.project_id || !cand?.contract_id) return;
    window.open(`${api.defaults.baseURL}/projects/${cand.project_id}/contracts/${cand.contract_id}/pdf`, "_blank");
  };

  useEffect(() => { fetchProjects(); fetchQueue(); }, []);
  useEffect(() => {
    if (selectedProjectId) fetchInvoices(selectedProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  // Hent PDF + audit når faktura velges + tab er pdf eller aktivitet
  useEffect(() => {
    if (!selectedInvoiceId) { setPdfUrl(null); return; }
    setPdfLoading(true);
    api.get(`/portfolio/invoices/${selectedInvoiceId}/pdf`, { responseType: "blob" })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setPdfUrl(url);
      })
      .catch(() => setPdfUrl(null))
      .finally(() => setPdfLoading(false));
    return () => {
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoiceId]);

  const fetchContractProgress = async (contractId: string) => {
    if (!selectedProjectId) return;
    setProgressLoading(true);
    try {
      const res = await api.get(`/projects/${selectedProjectId}/contracts/${contractId}/progress`);
      setContractProgress(res.data);
    } catch { setContractProgress(null); }
    finally { setProgressLoading(false); }
  };

  // Handlinger
  const handleLineAction = async (invoiceId: string, linjeNr: number, action: "approve" | "reject", kommentar?: string) => {
    const key = `${invoiceId}-${linjeNr}`;
    setLineActionLoading(key);
    try {
      await api.post(`/projects/${selectedProjectId}/invoices/${invoiceId}/lines/${linjeNr}/${action}`, {
        kommentar: kommentar || undefined,
      });
      fetchInvoices();
      setShowRejectForm(null);
    } catch (e: any) {
      // E1: backend avviser godkjenning av flagget linje uten begrunnelse (400) —
      // vis det i stedet for å feile stille.
      setUploadMessage({ text: getErrorMessage(e, t("invoice.begrunnelse_pakrevd")), ok: false });
    } finally { setLineActionLoading(null); }
  };

  // Bulk: godkjenn alle OK/GODKJENT_CO-linjer som ikke alt er behandlet
  const approveAllOk = async () => {
    if (!selectedInvoice || !selectedProjectId) return;
    const pending = (selectedInvoice.analysis?.linjeanalyse || []).filter((l: any) =>
      (l.status === "OK" || l.status === "GODKJENT_CO") && !selectedInvoice.line_approvals?.[l.linje_nr]
    );
    if (pending.length === 0) return;
    setBulkApproving(true);
    try {
      for (const l of pending) {
        await api.post(`/projects/${selectedProjectId}/invoices/${selectedInvoice.id}/lines/${l.linje_nr}/approve`, {});
      }
      await fetchInvoices();
    } catch (e: any) {
      setUploadMessage({ text: getErrorMessage(e, "Feil ved godkjenning"), ok: false });
    } finally { setBulkApproving(false); }
  };

  const undoLineApproval = async (invoiceId: string, linjeNr: number) => {
    const key = `${invoiceId}-${linjeNr}`;
    setLineActionLoading(key);
    try {
      await api.delete(`/projects/${selectedProjectId}/invoices/${invoiceId}/lines/${linjeNr}/approval`);
      fetchInvoices();
    } catch {} finally { setLineActionLoading(null); }
  };

  // Aggregert faktura-beslutning (commit ved full godkjenning, ellers notis til ERP)
  const submitDecision = async (inv: any) => {
    setDecisionLoading(true);
    try {
      const res = await api.post(`/projects/${inv.project_id}/invoices/${inv.id}/decision`);
      const approved = res.data.decision === "APPROVED";
      setUploadMessage({
        text: approved ? t("invoice.decision_approved_ok") : t("invoice.decision_rejected_ok"),
        ok: approved,
      });
      fetchInvoices();
    } catch (e: any) {
      if (e?.response?.status === 409) setUploadMessage({ text: t("invoice.decision_conflict"), ok: false });
      else setUploadMessage({ text: getErrorMessage(e, t("invoice.decision_error")), ok: false });
    } finally { setDecisionLoading(false); }
  };

  // Meldings-språk: default = grensesnitt-språket, men overstyrbart i utkast-arket.
  const uiLang = i18n.language?.startsWith("en") ? "en" : "nb";
  const generateRejectionEmail = async (invoiceId: string, language?: string) => {
    // Sjekk om det finnes avviste linjer før vi kaller API
    const hasRejected = Object.values(selectedInvoice?.line_approvals || {}).some((la: any) => la?.status === "avvist");
    if (!hasRejected) {
      setUploadMessage({ text: t("invoice.avvis_forst_info"), ok: false });
      return;
    }
    const lang = language || uiLang;
    setDraftLang(lang);
    setDraftLoading(true);
    try {
      const res = await api.post(`/projects/${selectedProjectId}/invoices/${invoiceId}/draft-rejection-email`, { language: lang });
      setDraftEmail({ ...res.data, invoice_id: invoiceId });
    } catch (e: any) {
      const msg = e?.response?.data?.detail || t("invoice.varsle_feil");
      setUploadMessage({ text: msg, ok: false });
    } finally { setDraftLoading(false); }
  };

  const downloadCSV = async () => {
    const params = selectedProjectId ? `?project_id=${selectedProjectId}` : "";
    const res = await api.get(`/reports/invoices/csv${params}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = "contrack_fakturaer.csv"; a.click();
  };

  const uploadInvoice = async () => {
    if (!uploadFile || !uploadContractId || !selectedProjectId) return;
    setUploading(true); setUploadMessage(null);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("contract_id", uploadContractId);
      const res = await api.post(`/projects/${selectedProjectId}/invoices/upload`, formData);
      setUploadMessage({ text: `${t("invoice.faktura_analysert")}: ${res.data.risikonivaa} ${t("invoice.risiko_lc")}`, ok: true });
      setShowUpload(false); setUploadFile(null); setUploadContractId("");
      fetchInvoices();
    } catch (e: any) {
      setUploadMessage({ text: getErrorMessage(e, "Feil ved opplasting"), ok: false });
    } finally { setUploading(false); }
  };

  // Derived
  const currentProject = projects.find(p => p.id === selectedProjectId);
  const vendors = currentProject?.vendors || [];
  const selectedInvoice = useMemo(() => invoices.find(i => i.id === selectedInvoiceId), [invoices, selectedInvoiceId]);

  useEffect(() => {
    if (selectedInvoice && (selectedInvoice.replacement_status || selectedInvoice.replaces_invoice_id)) {
      fetchReplacement(selectedInvoice.id);
    } else { setReplacementInfo(null); }
    setShowLinkPicker(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoiceId]);

  // Drill-down fra leverandøranalyse: ?line=N → bytt til analyse-fanen, scroll til + uthev linjen
  useEffect(() => {
    if (!urlLine || !selectedInvoice?.analysis) return;
    const nr = Number(urlLine);
    setActiveTab("analyse");
    const tmr = setTimeout(() => {
      const el = document.getElementById(`inv-line-${nr}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightLine(nr);
        setTimeout(() => setHighlightLine(null), 2600);
      }
    }, 300);
    return () => clearTimeout(tmr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLine, selectedInvoice?.id]);

  // Auto-select første hvis ingen valgt
  useEffect(() => {
    if (!selectedInvoiceId && invoices.length > 0) setSelectedInvoiceId(invoices[0].id);
    if (selectedInvoiceId && !invoices.find(i => i.id === selectedInvoiceId)) {
      setSelectedInvoiceId(invoices[0]?.id || null);
    }
  }, [invoices, selectedInvoiceId]);

  // Hent kontrakt-progress når kontraktstatus-tab åpnes
  useEffect(() => {
    if (activeTab === "kontrakt" && selectedInvoice?.contract_id) {
      fetchContractProgress(selectedInvoice.contract_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedInvoice?.contract_id]);

  const inlineStatus = (s: string) => {
    const map: Record<string, { dot: string; label: string }> = {
      approved: { dot: "bg-pos", label: t("common.godkjent") },
      godkjent: { dot: "bg-pos", label: t("common.godkjent") },
      betalt: { dot: "bg-faint", label: t("invoice.betalt") },
      delvis_godkjent: { dot: "bg-info", label: t("invoice.delvis") },
      flagged: { dot: "bg-neg", label: t("invoice.flagget") },
    };
    const m = map[s] || { dot: "bg-flag", label: t("common.venter") };
    return (
      <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full border border-line bg-canvas text-muted">
        <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
        {m.label}
      </span>
    );
  };

  const renderLineActions = (linje: any) => {
    if (!selectedInvoice) return null;
    const la = selectedInvoice.line_approvals?.[linje.linje_nr];
    const key = `${selectedInvoice.id}-${linje.linje_nr}`;
    const busy = lineActionLoading === key;
    const openComment = () => {
      setRejectComment(prev => ({ ...prev, [key]: la?.kommentar || "" }));
      setShowRejectForm(key);
    };
    return (
      <div className="flex items-center gap-1 justify-end flex-wrap">
        {la?.status === "godkjent" ? (
          <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-pos mr-1"><CheckCircle className="w-3.5 h-3.5" /> {t("common.godkjent")}</span>
        ) : la?.status === "avvist" ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-neg mr-1"><XCircle className="w-3.5 h-3.5" /> {t("common.avvist")}</span>
            <button onClick={openComment}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-[8px] text-[11.5px] font-medium text-muted hover:text-night hover:bg-black/[0.04] transition-colors">
              <MessageSquarePlus className="w-3.5 h-3.5" /> {la?.kommentar ? t("invoice.rediger_kommentar") : t("invoice.legg_til_kommentar")}
            </button>
          </>
        ) : (
          <>
            {/* E1: grønn linje → ett klikk; flagget linje → åpne begrunnelse-form (obligatorisk). */}
            <button onClick={() => isLineGreen(linje) ? handleLineAction(selectedInvoice.id, linje.linje_nr, "approve") : openComment()} disabled={busy}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-[8px] text-[11.5px] font-semibold text-pos hover:bg-pos/10 transition-colors disabled:opacity-50">
              {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} {t("invoice.godkjenn")}
            </button>
            <button onClick={() => handleLineAction(selectedInvoice.id, linje.linje_nr, "reject")} disabled={busy}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-[8px] text-[11.5px] font-semibold text-neg hover:bg-neg/10 transition-colors disabled:opacity-50">
              <XCircle className="w-3.5 h-3.5" /> {t("invoice.avvis_kort")}
            </button>
          </>
        )}
        {la && (
          <button onClick={() => undoLineApproval(selectedInvoice.id, linje.linje_nr)}
            className="inline-flex items-center justify-center w-7 h-7 rounded-[8px] text-faint hover:text-night hover:bg-black/[0.05] transition-colors" title={t("invoice.angre_handling", { defaultValue: "Angre" })}>
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  const lineConfig: Record<string, any> = {
    OK: { dot: "bg-pos", label: t("common.ok"), Icon: CheckCircle, tone: "text-pos", note: t("invoice.analyse_ok") },
    AVVIK: { dot: "bg-neg", label: t("common.avvik"), Icon: AlertTriangle, tone: "text-neg", note: t("invoice.analyse_avvik") },
    UDOKUMENTERT: { dot: "bg-neg", label: t("invoice.udokumentert"), Icon: AlertTriangle, tone: "text-neg", note: t("invoice.analyse_udok") },
    GODKJENT_CO: { dot: "bg-info", label: t("invoice.endringsordre"), Icon: Info, tone: "text-info", note: t("invoice.analyse_co") },
  };

  // ── Render ──
  return (
    <div className="flex flex-col h-[calc(100vh-52px)]">
      {/* Toast */}
      {uploadMessage && (
        <div className={`fixed top-[60px] left-4 right-4 sm:left-auto sm:right-6 z-50 rounded-[14px] px-4 py-3 text-[13px] font-semibold shadow-pop ${uploadMessage.ok ? "bg-pos text-white" : "bg-neg text-white"}`}>
          {uploadMessage.text}
          <button onClick={() => setUploadMessage(null)} className="ml-3 opacity-70 hover:opacity-100">×</button>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* ── VENSTRE PANEL: Liste ── */}
        <aside className={`${selectedInvoiceId ? "hidden md:flex" : "flex"} w-full md:w-[320px] shrink-0 border-r border-line flex-col overflow-hidden`}>
          <div className="px-5 pt-5 sm:pt-8 pb-3">
            {urlProject && (
              <button onClick={() => navigate(`/projects/${urlProject}`)}
                className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-night transition-colors mb-3">
                <ArrowLeft className="w-4 h-4" /> {currentProject?.name || t("detail.back")}
              </button>
            )}
            <h1 className="text-[26px] sm:text-[28px] font-extrabold text-night leading-none tracking-tight mb-3">{t("invoice.fakturaer")}</h1>
            {currentProject && (
              <div className="relative">
                <button onClick={() => projects.length > 1 && setShowProjectMenu(v => !v)}
                  disabled={projects.length <= 1}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-canvas border border-line hover:bg-black/[0.03] active:scale-[0.985] rounded-[10px] transition-all group">
                  <FolderOpen className="w-4 h-4 text-muted shrink-0" />
                  <span className="flex-1 text-left text-[13px] font-semibold text-night truncate">{currentProject.name}</span>
                  {projects.length > 1 && <ChevronDown className={`w-3.5 h-3.5 text-faint shrink-0 transition-transform ${showProjectMenu ? "rotate-180" : ""}`} />}
                </button>
                {showProjectMenu && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowProjectMenu(false)} />
                    <div className="absolute left-0 right-0 top-[44px] z-30 bg-card rounded-[12px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] border py-1 animate-fade-up overflow-hidden"
                      style={{ borderColor: "rgba(60,60,67,0.10)" }}>
                      {projects.map(p => (
                        <button key={p.id} onClick={() => {
                          setSelectedProjectId(p.id); setSelectedInvoiceId(null); fetchInvoices(p.id); setShowProjectMenu(false);
                        }} className="w-full flex items-center justify-between px-3 py-2.5 text-[13px] text-night hover:bg-black/[0.04] transition-colors text-left">
                          <span className={`truncate ${p.id === selectedProjectId ? "font-semibold text-night" : ""}`}>{p.name}</span>
                          {p.id === selectedProjectId && <Check className="w-4 h-4 text-night shrink-0 ml-2" />}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Search + filter */}
          <div className="px-5 pb-3 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 text-faint absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t("common.sok")}
                className="w-full pl-8 pr-3 py-1.5 bg-black/[0.05] rounded-[8px] text-[13px] text-night placeholder:text-faint focus:bg-white focus:ring-[3px] focus:ring-night/10 focus:outline-none transition-all" />
            </div>
            <div className="relative">
              <button onClick={() => setShowFilterMenu(v => !v)}
                className={`w-8 h-8 rounded-[8px] flex items-center justify-center transition-all relative ${
                  statusFilter ? "bg-night text-white" : "bg-black/[0.05] text-muted hover:bg-black/[0.08]"
                }`} title={t("common.filtrer")}>
                <SlidersHorizontal className="w-3.5 h-3.5" />
              </button>
              {showFilterMenu && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setShowFilterMenu(false)} />
                  <div className="absolute right-0 top-10 z-30 w-48 bg-card rounded-[12px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-b border-line py-1 animate-fade-up"
                    style={{ borderColor: "rgba(60,60,67,0.10)" }}>
                    {FILTERS.map(f => (
                      <button key={f.value} onClick={() => { setStatusFilter(f.value); setShowFilterMenu(false); }}
                        className="w-full flex items-center justify-between px-3 py-2 text-[13px] text-night hover:bg-black/[0.04] transition-colors">
                        <span className={statusFilter === f.value ? "font-semibold text-night" : ""}>{f.label}</span>
                        {statusFilter === f.value && <Check className="w-4 h-4 text-night" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {erpQueue.length > 0 && (
            <button onClick={() => setShowQueue(true)}
              className="mx-5 mb-3 flex items-center justify-between gap-2 px-3 py-2 rounded-[10px] bg-flag/10 text-flag text-[12.5px] font-semibold hover:bg-flag/[0.16] transition-colors">
              <span className="inline-flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> {t("invoice.til_avklaring", { count: erpQueue.length })}</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}

          <div className="border-t border-line flex-1 overflow-y-auto">
            {loading && invoices.length === 0 ? (
              <div className="flex justify-center py-10"><Loader className="w-5 h-5 animate-spin text-faint" /></div>
            ) : invoices.length === 0 ? (
              <p className="text-[13px] text-faint text-center py-10 px-5">{t("invoice.ingen_fakturaer")}</p>
            ) : invoices.map(inv => {
              const active = selectedInvoiceId === inv.id;
              const risk = inv.analysis?.risikonivaa;
              const dotColor = risk === "HØY" ? "red" : risk === "MIDDELS" ? "amb" : "grn";
              return (
                <button key={inv.id} onClick={() => setSelectedInvoiceId(inv.id)}
                  className={`w-full text-left px-5 py-3.5 border-b border-line transition-colors ${active ? "bg-night/[0.05]" : "hover:bg-black/[0.02]"}`}>
                  <div className="flex items-start gap-3">
                    <Avatar name={inv.vendor_name || "?"} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-[13.5px] font-semibold text-night truncate">{inv.vendor_name}</p>
                        <p className="text-[13.5px] font-semibold text-night tabular shrink-0">{Math.round(inv.total_amount || 0).toLocaleString()}</p>
                      </div>
                      <p className="text-[11.5px] text-faint tabular truncate mt-0.5">{inv.invoice_number} · {inv.invoice_date?.slice(0, 10) || "–"}</p>
                      {inv.analysis?.avvik > 0 && (
                        <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium text-muted">
                          <span className="w-1.5 h-1.5 rounded-full bg-neg" />
                          {t("invoice.avvik")} <span className="font-semibold text-neg tabular">−{Math.round(inv.analysis.avvik).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                    {risk && <StatusDot color={dotColor as any} size="sm" pulse={risk !== "LAV"} className="mt-1" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Bottom actions */}
          <div className="border-t border-line px-3 py-3 flex gap-2">
            <button onClick={() => setShowUpload(true)} className="flex-1 cx-btn-ghost !py-2 !text-[12px]">
              <Upload className="w-3.5 h-3.5" /> {t("common.last_opp")}
            </button>
            <button onClick={downloadCSV} className="cx-btn-ghost !py-2 !text-[12px]" title="Eksporter CSV">
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>
        </aside>

        {/* ── HØYRE PANEL: Detalj ── */}
        <main className={`${selectedInvoiceId ? "flex" : "hidden md:flex"} flex-1 overflow-y-auto bg-card min-w-0 flex-col`}>
          {!selectedInvoice ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-[14px] text-faint">{t("invoice.velg_faktura")}</p>
            </div>
          ) : (
            <div className="px-4 sm:px-6 md:px-8 pt-5 md:pt-9 pb-12 animate-fade-up" key={selectedInvoice.id}>
              <button onClick={() => setSelectedInvoiceId(null)} className="md:hidden mb-4 inline-flex items-center gap-1 text-[14px] font-semibold text-muted -ml-2 px-2 py-2 rounded-[10px] active:bg-black/[0.05]">
                <ChevronLeft className="w-5 h-5" /> {t("invoice.fakturaer")}
              </button>
              {/* Vendor hero */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
                <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
                  <div className="hidden sm:block"><Avatar name={selectedInvoice.vendor_name || "?"} size="xl" /></div>
                  <div className="sm:hidden"><Avatar name={selectedInvoice.vendor_name || "?"} size="lg" /></div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-[19px] sm:text-[24px] font-extrabold text-night leading-tight break-words">{selectedInvoice.vendor_name}</h2>
                    <p className="text-[12px] sm:text-[13px] text-muted mt-1 break-all">
                      <span className="tabular font-semibold">{selectedInvoice.invoice_number}</span>
                      {selectedInvoice.invoice_date && ` · ${new Date(selectedInvoice.invoice_date).toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" })}`}
                    </p>
                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                      {inlineStatus(selectedInvoice.status)}
                      {selectedInvoice.committed && (
                        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full border border-line bg-canvas text-muted">
                          <span className="w-1.5 h-1.5 rounded-full bg-pos" />{t("invoice.bokfort")}
                        </span>
                      )}
                      {selectedInvoice.erp_external_id && (
                        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full border border-line bg-canvas text-muted">
                          <span className={`w-1.5 h-1.5 rounded-full ${selectedInvoice.erp_decision === "APPROVED" ? "bg-pos" : "bg-info"}`} />
                          Tripletex{selectedInvoice.erp_decision === "APPROVED" ? ` · ${t("invoice.erp_attestert")}` : selectedInvoice.erp_decision === "PENDING" ? ` · ${t("invoice.erp_pending")}` : ""}
                        </span>
                      )}
                      {selectedInvoice.erp_writeback_error && (
                        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full bg-neg/10 text-neg">
                          <AlertTriangle className="w-3 h-3" />{t("invoice.erp_feil")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="sm:shrink-0 w-full sm:w-auto flex flex-col gap-2">
                  {selectedInvoice.analysis && !selectedInvoice.committed && selectedInvoice.status !== "betalt" && (() => {
                    const lns = selectedInvoice.analysis.linjeanalyse || [];
                    const reviewed = lns.filter((l: any) => selectedInvoice.line_approvals?.[l.linje_nr]).length;
                    const allReviewed = lns.length > 0 && reviewed === lns.length;
                    return (
                      <button onClick={() => submitDecision(selectedInvoice)} disabled={!allReviewed || decisionLoading}
                        className="cx-btn-dark w-full !py-2.5 !text-[14px] disabled:opacity-50">
                        {decisionLoading ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                        {allReviewed ? t("invoice.send_beslutning") : t("invoice.vurder_alle_forst", { reviewed, total: lns.length })}
                      </button>
                    );
                  })()}
                  {selectedInvoice.analysis?.avvik > 0 && (
                    <Button variant="secondary" loading={draftLoading} icon={<Mail className="w-4 h-4" />} onClick={() => generateRejectionEmail(selectedInvoice.id)} fullWidth>
                      {t("invoice.varsle_leverandor")}
                    </Button>
                  )}
                  {replacementInfo?.replacement_status !== "confirmed" && (
                    <button onClick={openLinkPicker} className="text-[12px] font-medium text-muted hover:text-night text-center">
                      {t("invoice.repl_link_manual")}
                    </button>
                  )}
                </div>
              </div>

              {/* 3 KPI */}
              <div className="grid grid-cols-3 cx-card overflow-hidden mb-6">
                <div className="px-3 sm:px-5 py-4 min-w-0">
                  <p className="text-label uppercase text-faint mb-1.5 truncate">{t("invoice.fakturert")}</p>
                  <p className="text-[18px] sm:text-[24px] font-bold text-night tabular leading-none truncate">{Math.round(selectedInvoice.total_amount || 0).toLocaleString()}</p>
                  <p className="text-[11px] text-faint mt-1.5">{selectedInvoice.currency || "NOK"}</p>
                </div>
                <div className="px-3 sm:px-5 py-4 border-l border-line min-w-0">
                  <p className="text-label uppercase text-faint mb-1.5 truncate">{t("invoice.avvik")}</p>
                  <p className={`text-[18px] sm:text-[24px] font-bold tabular leading-none truncate ${selectedInvoice.analysis?.avvik > 0 ? "text-neg" : "text-night"}`}>
                    {selectedInvoice.analysis?.avvik > 0 ? Math.round(selectedInvoice.analysis.avvik).toLocaleString() : "0"}
                  </p>
                  <p className="text-[11px] text-faint mt-1.5 truncate">
                    {selectedInvoice.analysis?.linjeanalyse ? `${selectedInvoice.analysis.linjeanalyse.filter((l: any) => l.status === "AVVIK" || l.status === "UDOKUMENTERT").length} linjer` : "—"}
                  </p>
                </div>
                <div className="px-3 sm:px-5 py-4 border-l border-line min-w-0">
                  <p className="text-label uppercase text-faint mb-1.5 truncate">{t("invoice.forfaller")}</p>
                  <p className="text-[15px] sm:text-[20px] font-bold text-night tabular leading-none truncate">
                    {selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString("nb-NO", { day: "numeric", month: "short" }) : "—"}
                  </p>
                  <p className="text-[11px] text-faint mt-1.5 truncate">
                    {selectedInvoice.analysis?.linjeanalyse ? `${selectedInvoice.analysis.linjeanalyse.filter((l: any) => l.status === "OK" || l.status === "GODKJENT_CO").length}/${selectedInvoice.analysis.linjeanalyse.length} OK` : "—"}
                  </p>
                </div>
              </div>

              {/* Faner */}
              <div className="border-b border-line mb-6 overflow-x-auto">
                <div className="flex gap-5 sm:gap-6 min-w-max">
                  {(["analyse", "kontrakt", "pdf"] as Tab[]).map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                      className={`tab-underline whitespace-nowrap ${activeTab === tab ? "active" : ""}`}>
                      <span className="sm:hidden">{tab === "analyse" ? t("invoice.analyse") : tab === "kontrakt" ? t("invoice.kontrakt") : t("invoice.pdf")}</span>
                      <span className="hidden sm:inline">{tab === "analyse" ? t("invoice.analyse") : tab === "kontrakt" ? t("invoice.kontraktstatus") : t("invoice.pdf_visning")}</span>
                      {tab === "analyse" && selectedInvoice.analysis?.linjeanalyse && (
                        <span className="ml-1.5 text-[10px] font-bold text-neg">
                          {selectedInvoice.analysis.linjeanalyse.filter((l: any) => l.status === "AVVIK" || l.status === "UDOKUMENTERT").length || ""}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab-innhold */}
              {/* A1: korrigert-faktura-banner */}
              {replacementInfo?.replacement_status === "suggested" && replacementInfo?.original_number && (
                <div className="cx-card p-4 mb-5 flex items-start gap-3 border-info/30">
                  <div className="w-8 h-8 rounded-[9px] bg-info/10 flex items-center justify-center shrink-0"><Info className="w-4 h-4 text-info" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-night">{t("invoice.repl_suggested", { number: replacementInfo.original_number, date: (replacementInfo.original_date || "").slice(0, 10) })}</p>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => replAction("confirm-replacement")} className="cx-btn-dark !py-1.5 !px-3 !text-[12.5px]">{t("invoice.repl_confirm")}</button>
                      <button onClick={() => replAction("dismiss-replacement")} className="cx-btn-ghost !py-1.5 !px-3 !text-[12.5px]">{t("invoice.repl_not_same")}</button>
                    </div>
                  </div>
                </div>
              )}
              {replacementInfo?.replacement_status === "confirmed" && replacementInfo?.original_number && (
                <div className="cx-card p-4 mb-5 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-[9px] bg-pos/10 flex items-center justify-center shrink-0"><CheckCircle className="w-4 h-4 text-pos" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-night">{t("invoice.repl_confirmed", { number: replacementInfo.original_number })}</p>
                    {replacementInfo.original_flagged_lines?.length > 0 && (
                      <p className="text-[12.5px] text-muted mt-0.5">{t("invoice.repl_check_lines", { lines: replacementInfo.original_flagged_lines.join(", ") })}</p>
                    )}
                  </div>
                  <button onClick={() => replAction("unlink-replacement")} className="text-[12px] font-medium text-muted hover:text-neg shrink-0">{t("invoice.repl_unlink")}</button>
                </div>
              )}

              {activeTab === "analyse" && (
                <>
                  {selectedInvoice.analysis ? (
                    <>
                      {selectedInvoice.analysis.linjeanalyse && (() => {
                        // BUG 2 / Alt 3: toppsammendrag bygges DETERMINISTISK fra strukturerte
                        // linjedata via i18n-mal (ikke lagret LLM-tekst) → oversettes med UI-språket.
                        const lns = selectedInvoice.analysis.linjeanalyse || [];
                        const avvikN = lns.filter((l: any) => l.status !== "OK" && l.status !== "GODKJENT_CO").length;
                        const harAvvik = avvikN > 0;
                        return (
                          <div className="cx-card p-4 mb-5 flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0 ${harAvvik ? "bg-neg/10" : "bg-pos/10"}`}>
                              {harAvvik ? <XCircle className="w-4 h-4 text-neg" /> : <CheckCircle className="w-4 h-4 text-pos" />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-night mb-0.5">
                                {harAvvik ? `${t("invoice.kontraktsavvik")}: ${avvikN} ${t("invoice.poster")}` : t("invoice.ingen_avvik")}
                              </p>
                              {harAvvik && (
                                <p className="text-[12.5px] text-muted leading-relaxed">{t("analyse.sammendrag", { avvik: avvikN, total: lns.length })}</p>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {(() => {
                        const lines = selectedInvoice.analysis.linjeanalyse || [];
                        const okCount = lines.filter((l: any) => l.status === "OK" || l.status === "GODKJENT_CO").length;
                        const avvikCount = lines.filter((l: any) => l.status === "AVVIK" || l.status === "UDOKUMENTERT").length;
                        const pendingOk = lines.filter((l: any) => (l.status === "OK" || l.status === "GODKJENT_CO") && !selectedInvoice.line_approvals?.[l.linje_nr]).length;
                        return (
                          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                            <div className="flex items-baseline gap-2.5 min-w-0">
                              <p className="text-label uppercase text-faint">{t("invoice.linjepost_analyse")}</p>
                              <span className="text-[12px] text-muted tabular">{t("invoice.linjer_ok_avvik", { ok: okCount, avvik: avvikCount })}</span>
                            </div>
                            {pendingOk > 0 && (
                              <button onClick={approveAllOk} disabled={bulkApproving}
                                className="cx-btn-ghost !py-1.5 !px-3 !text-[12.5px] disabled:opacity-50">
                                {bulkApproving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 text-pos" />}
                                {bulkApproving ? t("invoice.godkjenner_alle") : `${t("invoice.godkjenn_alle_ok")} (${pendingOk})`}
                              </button>
                            )}
                          </div>
                        );
                      })()}

                      {/* Linje-kort — én lik struktur per linje */}
                      <div className="space-y-3">
                        {selectedInvoice.analysis.linjeanalyse?.map((linje: any, i: number) => {
                          const cfg = lineConfig[linje.status] || lineConfig.OK;
                          const AnIcon = cfg.Icon;
                          const key = `${selectedInvoice.id}-${linje.linje_nr}`;
                          const la = selectedInvoice.line_approvals?.[linje.linje_nr];
                          // BUG 2 / Alt 3: rendr forklaring fra språknøytral key+params via i18n-mal
                          // når regel-laget ga en key; ellers fri LLM-skjønnstekst, ellers status-etikett.
                          const note = linje.forklaring_key
                            ? t(`analyse.${linje.forklaring_key}`, linje.forklaring_params || {})
                            : (linje.forklaring || cfg.note);
                          const refLabel = linje.referanse && linje.referanse !== "Ingen" ? linje.referanse : null;
                          const fig = (label: string, value: string, cls = "text-night") => (
                            <div className="min-w-0">
                              <p className="text-[10.5px] font-medium uppercase tracking-[0.05em] text-faint truncate">{label}</p>
                              <p className={`text-[14px] font-semibold tabular mt-1 truncate ${cls}`}>{value}</p>
                            </div>
                          );
                          return (
                            <div key={i} id={`inv-line-${linje.linje_nr}`} className={`rounded-[12px] border bg-card p-4 sm:p-5 transition-shadow ${highlightLine === linje.linje_nr ? "ring-2 ring-info/60 border-info/40" : la?.status === "avvist" ? "border-neg/25" : "border-line"}`}>
                              <div className="flex items-start gap-3.5">
                                <span className="text-[12px] font-medium text-faint tabular pt-px w-4 text-right shrink-0">{linje.linje_nr}</span>
                                <div className="flex-1 min-w-0">
                                  {/* Header */}
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-[14px] font-semibold text-night leading-snug">{linje.beskrivelse}</p>
                                      {refLabel && <p className="text-[11.5px] text-faint mt-0.5">{t("invoice.ref")} {refLabel}</p>}
                                    </div>
                                    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted shrink-0 pt-0.5">
                                      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
                                    </span>
                                  </div>

                                  {/* Beløp */}
                                  <div className="grid grid-cols-3 gap-4 mt-3.5 max-w-sm">
                                    {fig(t("invoice.forventet"), linje.forventet_beloep != null ? Math.round(linje.forventet_beloep).toLocaleString() : "—", "text-muted")}
                                    {fig(t("invoice.fakturert"), Math.round(linje.fakturert_beloep || 0).toLocaleString())}
                                    {fig(t("invoice.avvik"), linje.avvik !== 0 ? `${linje.avvik > 0 ? "+" : ""}${Math.round(linje.avvik).toLocaleString()}` : "—", linje.avvik > 0 ? "text-neg" : "text-faint")}
                                  </div>

                                  {/* Analyse — alltid synlig */}
                                  <div className="flex items-start gap-2 mt-3.5">
                                    <AnIcon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${cfg.tone}`} />
                                    <p className="text-[12.5px] text-muted leading-relaxed">
                                      {linje.avvikstype && <span className="font-medium text-night">{t(`avvikstype.${linje.avvikstype}`, { defaultValue: linje.avvikstype })}. </span>}
                                      {note}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Handling */}
                              <div className="flex items-center justify-end gap-2 mt-3.5 pt-3 border-t border-line/70">
                                {renderLineActions(linje)}
                              </div>
                              {showRejectForm === key && (
                                <div className="mt-2.5 pt-3 border-t border-line/70">
                                  <p className="text-[11.5px] text-faint mb-1.5">{t("invoice.kommentar_hjelp")}</p>
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                    <input value={rejectComment[key] || ""} onChange={e => setRejectComment({ ...rejectComment, [key]: e.target.value })}
                                      placeholder={isLineGreen(linje) ? t("invoice.kommentar_valgfri") : t("invoice.begrunnelse_placeholder")} autoFocus className="field-ios !py-1.5 text-[12px] flex-1" />
                                    <div className="flex gap-2 shrink-0">
                                      {!isLineGreen(linje) && (
                                        <Button variant="primary" onClick={() => handleLineAction(selectedInvoice.id, linje.linje_nr, "approve", rejectComment[key])}>{t("invoice.godkjenn")}</Button>
                                      )}
                                      <Button variant="neutral" onClick={() => handleLineAction(selectedInvoice.id, linje.linje_nr, "reject", rejectComment[key])}>{t("invoice.avvis_kort")}</Button>
                                      <Button variant="neutral" onClick={() => setShowRejectForm(null)}>{t("common.avbryt")}</Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <p className="text-[13px] text-faint text-center py-10">{t("invoice.ingen_analyse")}</p>
                  )}
                </>
              )}

              {activeTab === "kontrakt" && (
                <>
                  {progressLoading ? (
                    <div className="flex justify-center py-10"><Loader className="w-5 h-5 animate-spin text-faint" /></div>
                  ) : contractProgress ? (
                    (() => {
                      const cv = Number(contractProgress.total_kontraktsverdi) || 0;
                      const ft = Number(contractProgress.total_fakturert) || 0;
                      const gt = Number(contractProgress.total_godkjent) || 0;
                      const pctGodkjent = cv > 0 ? (gt / cv) * 100 : 0;
                      const pctFakturert = cv > 0 ? (ft / cv) * 100 : 0;
                      const venterAmount = Math.max(0, ft - gt);
                      const gjenstaarAmount = Math.max(0, cv - ft);
                      const cur = selectedInvoice.currency || "NOK";
                      return (
                    <div>
                      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-[18px] sm:text-[20px] font-extrabold text-night break-words">{contractProgress.vendor_name || selectedInvoice.vendor_name}</p>
                          <p className="text-[12px] text-muted">{t("invoice.kontraktsverdi")} {cv > 0 ? fmt(cv, cur) : "—"}</p>
                        </div>
                        <p className={`text-[24px] sm:text-[28px] font-extrabold tabular shrink-0 ${cv > 0 ? "text-pos" : "text-faint"}`}>
                          {cv > 0 ? `${pctGodkjent.toFixed(1)}%` : "—"}
                        </p>
                      </div>
                      {cv > 0 ? (
                        <div className="relative h-1.5 rounded-full bg-black/[0.06] overflow-hidden mb-2">
                          {/* Amber: fakturert (ikke godkjent ennå) */}
                          <div className="absolute inset-y-0 left-0 bg-flag transition-all" style={{ width: `${Math.min(100, pctFakturert)}%` }} />
                          {/* Grønn: godkjent (over amber) */}
                          <div className="absolute inset-y-0 left-0 bg-pos transition-all" style={{ width: `${Math.min(100, pctGodkjent)}%` }} />
                        </div>
                      ) : (
                        <p className="text-[12px] text-faint italic mb-2">{t("invoice.mangler_kontraktsdata")}</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] sm:text-[12px] text-muted mb-6">
                        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-pos" /> Godkjent <span className="font-bold text-pos tabular">{fmt(gt, cur)}</span></span>
                        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-flag" /> Venter <span className="font-bold text-flag tabular">{fmt(venterAmount, cur)}</span></span>
                        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-black/[0.15]" /> Gjenstår <span className="font-bold text-night tabular">{fmt(gjenstaarAmount, cur)}</span></span>
                      </div>

                      <SectionLabel>{t("invoice.kontraktsposter")}</SectionLabel>
                      <div>
                        {(contractProgress.poster || []).map((post: any, i: number) => {
                          const pkt = Number(post.kontraktsverdi) || 0;
                          const pft = Number(post.fakturert_totalt) || 0;
                          const pgt = Number(post.godkjent_totalt) || 0;
                          const pctG = pkt > 0 ? Math.min(100, (pgt / pkt) * 100) : 0;
                          const pctF = pkt > 0 ? Math.min(100, (pft / pkt) * 100) : 0;
                          return (
                            <div key={i} className="border-b border-line py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-6 text-center shrink-0"><p className="text-[11px] font-bold text-faint tabular">{post.post_nr || i + 1}</p></div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[13px] font-semibold text-night truncate">{post.beskrivelse}</p>
                                  <p className="text-[11px] text-faint tabular truncate">{fmt(pgt, cur)} av {fmt(pkt, cur)}</p>
                                </div>
                                <p className={`text-[12px] font-bold tabular shrink-0 ${pctG > 0 ? "text-pos" : "text-faint"}`}>{pctG.toFixed(0)}%</p>
                              </div>
                              <div className="ml-9 mt-1.5 relative h-1 rounded-full bg-black/[0.06]">
                                <div className="absolute inset-y-0 left-0 bg-flag rounded-full transition-all" style={{ width: `${pctF}%` }} />
                                <div className="absolute inset-y-0 left-0 bg-pos rounded-full transition-all" style={{ width: `${pctG}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                      );
                    })()
                  ) : (
                    <p className="text-[13px] text-faint text-center py-10">{t("invoice.ingen_kontraktsdata")}</p>
                  )}
                </>
              )}

              {activeTab === "pdf" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Venstre: PDF */}
                  <div className="rounded-[14px] overflow-hidden border h-[60vh] lg:h-[calc(100vh-380px)]" style={{ borderColor: "rgba(60,60,67,0.10)" }}>
                    {pdfLoading ? (
                      <div className="h-full flex items-center justify-center"><Loader className="w-6 h-6 animate-spin text-faint" /></div>
                    ) : pdfUrl ? (
                      <iframe src={pdfUrl} className="w-full h-full" title="Faktura PDF" />
                    ) : (
                      <div className="h-full flex items-center justify-center text-[13px] text-faint">{t("invoice.pdf_ikke_tilgjengelig")}</div>
                    )}
                  </div>
                  {/* Høyre: AI-analyse med inline godkjenning */}
                  <div className="rounded-[14px] border overflow-y-auto h-[60vh] lg:h-[calc(100vh-380px)]" style={{ borderColor: "rgba(60,60,67,0.10)" }}>
                    <div className="px-4 py-3 border-b border-line flex items-center gap-2 sticky top-0 bg-card z-10">
                      <StatusDot color="blue" size="sm" pulse={false} />
                      <p className="text-[13px] font-bold text-night">AI-analyse</p>
                    </div>
                    {selectedInvoice.analysis ? (
                      <div className="p-3">
                        {selectedInvoice.analysis.avvik_forklaring && (
                          <div className={`rounded-[10px] p-3 mb-3 text-[12px] ${selectedInvoice.analysis.avvik > 0 ? "bg-neg/[0.06] text-neg" : "bg-pos/[0.06] text-pos"}`}>
                            {selectedInvoice.analysis.avvik_forklaring}
                          </div>
                        )}
                        {selectedInvoice.analysis.linjeanalyse?.map((linje: any, i: number) => {
                          const la = selectedInvoice.line_approvals?.[linje.linje_nr];
                          const cfg = lineConfig[linje.status] || lineConfig.OK;
                          const key = `${selectedInvoice.id}-${linje.linje_nr}`;
                          const isProblem = linje.status !== "OK" && linje.status !== "GODKJENT_CO";

                          // Brukerens godkjenningsstatus overstyrer AI-status
                          let statusLabel: string;
                          let statusCls: string;
                          if (la?.status === "godkjent") {
                            statusLabel = "✓ Godkjent";
                            statusCls = "text-pos";
                          } else if (la?.status === "avvist") {
                            statusLabel = "✗ Avvist";
                            statusCls = "text-neg";
                          } else if (isProblem) {
                            statusLabel = cfg.label;
                            statusCls = cfg.text;
                          } else {
                            statusLabel = "Venter";
                            statusCls = "text-flag";
                          }

                          return (
                            <div key={i} className="border-b border-line py-3">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="text-[13px] font-bold text-night leading-snug">{linje.linje_nr} {linje.beskrivelse}</p>
                                <span className={`text-[10px] font-bold uppercase shrink-0 ${statusCls}`}>
                                  {statusLabel}
                                </span>
                              </div>
                              {isProblem && linje.forklaring && <p className="text-[11.5px] text-muted mb-2">{linje.forklaring}</p>}
                              {linje.avvik !== 0 && (
                                <p className="text-[11.5px] font-bold text-neg tabular mb-2">Avvik: {linje.avvik > 0 ? "+" : ""}{fmt(linje.avvik, selectedInvoice.currency || "NOK")}</p>
                              )}
                              <div className="mt-1">
                                {renderLineActions(linje)}
                                {showRejectForm === key && (
                                  <div className="mt-2 flex flex-col gap-1.5">
                                    <input value={rejectComment[key] || ""} onChange={e => setRejectComment({ ...rejectComment, [key]: e.target.value })}
                                      placeholder={t("invoice.kommentar_valgfri")} autoFocus className="field-ios !py-1.5 text-[12px]" />
                                    <div className="flex gap-1.5">
                                      <Button variant="primary" onClick={() => handleLineAction(selectedInvoice.id, linje.linje_nr, "reject", rejectComment[key])}>{t("common.lagre")}</Button>
                                      <Button variant="neutral" onClick={() => setShowRejectForm(null)}>{t("common.avbryt")}</Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-[13px] text-faint text-center py-10">{t("invoice.ingen_analyse")}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Sheet: Last opp faktura */}
      <Sheet open={showUpload} onClose={() => { setShowUpload(false); setUploadFile(null); setUploadContractId(""); }}
        title={t("invoice.last_opp_faktura")} subtitle={currentProject?.name} blur>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("invoice.leverandor")}</label>
            <select value={uploadContractId} onChange={e => setUploadContractId(e.target.value)} className="field-ios">
              <option value="">{t("security.velg_leverandor")}</option>
              {vendors.map((v: any) => <option key={v.contract_id} value={v.contract_id}>{v.vendor_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("invoice.pdf_fil")}</label>
            <input type="file" accept=".pdf" onChange={e => setUploadFile(e.target.files?.[0] || null)}
              className="w-full text-[13px] text-night file:mr-3 file:py-2 file:px-3 file:rounded-[10px] file:border-0 file:bg-night file:text-white file:font-semibold file:cursor-pointer" />
          </div>
          <div className="space-y-2 pt-2">
            <Button variant="primary" fullWidth loading={uploading} onClick={uploadInvoice} disabled={!uploadFile || !uploadContractId}>
              {t("invoice.last_opp_og_analyser")}
            </Button>
            <Button variant="neutral" fullWidth onClick={() => { setShowUpload(false); setUploadFile(null); setUploadContractId(""); }}>
              {t("common.avbryt")}
            </Button>
          </div>
        </div>
      </Sheet>

      {/* Sheet: E-postutkast */}
      <Sheet open={!!draftEmail} onClose={() => setDraftEmail(null)} title={t("invoice.epost_utkast")} subtitle={draftEmail?.to} blur>
        <div className="space-y-4 mt-4">
          {/* Meldings-språk (default = grensesnitt, overstyrbart). Bytte re-genererer utkastet. */}
          <div className="flex items-center justify-between">
            <label className="text-[12px] font-semibold text-muted">{t("invoice.meldingssprak")}</label>
            <div className="flex bg-canvas border border-line rounded-[9px] p-0.5">
              {["nb", "en"].map(lng => (
                <button key={lng} disabled={draftLoading}
                  onClick={() => { if (lng !== draftLang && draftEmail?.invoice_id) generateRejectionEmail(draftEmail.invoice_id, lng); }}
                  className={`px-3 py-1 rounded-[6px] text-[12px] font-bold transition-all disabled:opacity-50 ${draftLang === lng ? "bg-white text-night shadow-sm" : "text-faint"}`}>
                  {lng === "nb" ? "NO" : "EN"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("invoice.emne")}</label>
            <input value={draftEmail?.subject || ""} readOnly className="field-ios" />
          </div>
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("invoice.melding")}</label>
            <textarea value={draftEmail?.draft || ""}
              onChange={e => setDraftEmail(draftEmail ? { ...draftEmail, draft: e.target.value } : null)}
              rows={12} className="field-ios resize-none" />
          </div>
          <div className="space-y-2 pt-2">
            {draftEmail && (
              <a href={`mailto:${draftEmail.to}?subject=${encodeURIComponent(draftEmail.subject)}&body=${encodeURIComponent(draftEmail.draft)}`}
                className="cx-btn-dark w-full">
                {t("invoice.apne_epostklient")}
              </a>
            )}
            <Button variant="secondary" fullWidth onClick={() => { if (draftEmail) navigator.clipboard.writeText(draftEmail.draft); }}>
              {t("invoice.kopier_tekst")}
            </Button>
            <Button variant="neutral" fullWidth onClick={() => setDraftEmail(null)}>{t("common.lukk")}</Button>
          </div>
        </div>
      </Sheet>

      {/* Koble til avvist faktura (manuell A1-lenking) */}
      {showLinkPicker && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={() => setShowLinkPicker(false)}>
          <div className="bg-card rounded-[16px] shadow-pop border border-line w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3.5 border-b border-line flex items-center justify-between shrink-0">
              <p className="text-[15px] font-bold text-night">{t("invoice.repl_picker_title")}</p>
              <button onClick={() => setShowLinkPicker(false)} className="w-8 h-8 rounded-[9px] flex items-center justify-center text-faint hover:text-night hover:bg-black/[0.05]"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto p-3">
              {linkCandidates.length === 0 ? (
                <p className="text-[13px] text-faint text-center py-8">{t("invoice.repl_picker_empty")}</p>
              ) : linkCandidates.map(c => (
                <button key={c.id} onClick={() => replAction("link-replacement", { replaces_invoice_id: c.id })}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-[10px] hover:bg-black/[0.03] transition-colors text-left">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-night tabular truncate">{c.invoice_number}</p>
                    <p className="text-[12px] text-faint tabular">{(c.invoice_date || "").slice(0, 10)} · {fmt(c.total_amount || 0, c.currency || "NOK")}</p>
                  </div>
                  <span className="cx-btn-dark !py-1.5 !px-3 !text-[12px] shrink-0">{t("invoice.repl_link_btn")}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Til avklaring — manuell ERP-match */}
      {showQueue && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={() => setShowQueue(false)}>
          <div className="bg-card rounded-[16px] shadow-pop border border-line w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3.5 border-b border-line flex items-center justify-between shrink-0">
              <div>
                <p className="text-[15px] font-bold text-night">{t("invoice.til_avklaring_title")}</p>
                <p className="text-[12px] text-muted mt-0.5">{t("invoice.til_avklaring_sub")}</p>
              </div>
              <button onClick={() => setShowQueue(false)} className="w-8 h-8 rounded-[9px] flex items-center justify-center text-faint hover:text-night hover:bg-black/[0.05] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-3">
              {erpQueue.length === 0 ? (
                <p className="text-[13px] text-faint text-center py-8">{t("invoice.til_avklaring_tom")}</p>
              ) : erpQueue.map(item => {
                const sel = queueSel[item.id] || { project_id: "", contract_id: "" };
                const proj = projects.find(p => p.id === sel.project_id);
                const vendors = proj?.vendors || [];
                const needsContract = item.match_status === "no_contract" || item.match_status === "ambiguous";
                const msLabel = t(`invoice.ms_${item.match_status}`, { defaultValue: item.match_status });
                return (
                  <div key={item.id} className="cx-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[14px] font-semibold text-night truncate">{item.vendor_name || "—"}</p>
                        <p className="text-[12px] text-faint tabular truncate">{item.invoice_number || "—"} · {fmt(item.total_amount || 0, item.currency || "NOK")}{item.vendor_org_number ? ` · ${item.vendor_org_number}` : ""}</p>
                      </div>
                      <span className="cx-chip bg-flag/10 text-flag shrink-0">{msLabel}</span>
                    </div>
                    {needsContract ? (
                      <>
                        {/* 8c: rangerte FORSLAG — vi tror dette, vi velger ikke stille.
                            Sterkeste kandidat fremhevet m/begrunnelse; ett-klikks bekreftelse. */}
                        {(item.candidates?.length > 0) && (
                          <div className="mt-3 space-y-1.5">
                            <SectionLabel>{t("invoice.foreslatte_kontrakter")}</SectionLabel>
                            {item.candidates.map((c: any) => {
                              const isSuggested = c.contract_id === item.suggested_contract_id;
                              return (
                                <div key={c.contract_id}
                                  className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 ${isSuggested ? "border-night/30 bg-night/[0.03]" : "border-line"}`}>
                                  <div className="min-w-0">
                                    <p className="text-[13px] font-medium text-night truncate">
                                      {c.vendor_name || "—"}{c.contract_ref ? ` · ${c.contract_ref}` : ""}
                                      {isSuggested && <span className="cx-chip bg-night/10 text-night ml-2">{t("invoice.mest_sannsynlig")}</span>}
                                    </p>
                                    <p className="text-[11.5px] text-muted truncate">{c.project_name} — {c.begrunnelse}</p>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <button onClick={() => viewContractPdf(c)} className="cx-btn-ghost !py-1.5 !text-[12px]">{t("invoice.vis_kontrakt")}</button>
                                    <button onClick={() => assignCandidate(item, c.contract_id)} disabled={assigning === item.id}
                                      className="cx-btn-dark !py-1.5 !text-[12px] disabled:opacity-50">{t("invoice.koble")}</button>
                                  </div>
                                </div>
                              );
                            })}
                            <p className="text-[11.5px] text-faint pt-0.5">{t("invoice.eller_velg_manuelt")}</p>
                          </div>
                        )}
                        <div className="grid sm:grid-cols-2 gap-2 mt-3">
                          <select value={sel.project_id} className="field-ios !py-2 text-[13px]"
                            onChange={e => setQueueSel({ ...queueSel, [item.id]: { project_id: e.target.value, contract_id: "" } })}>
                            <option value="">{t("security.velg_prosjekt")}</option>
                            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                          <select value={sel.contract_id} disabled={!sel.project_id} className="field-ios !py-2 text-[13px]"
                            onChange={e => setQueueSel({ ...queueSel, [item.id]: { ...sel, contract_id: e.target.value } })}>
                            <option value="">{t("security.velg_leverandor")}</option>
                            {vendors.map((v: any) => <option key={v.contract_id} value={v.contract_id}>{v.vendor_name}</option>)}
                          </select>
                        </div>
                        <div className="flex justify-end mt-2.5">
                          <button onClick={() => assignContract(item)} disabled={!sel.contract_id || assigning === item.id}
                            className="cx-btn-dark !py-2 !text-[13px] disabled:opacity-50">
                            {assigning === item.id ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            {t("invoice.koble_valider")}
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="text-[12px] text-muted mt-2 leading-relaxed">{t(`invoice.ms_${item.match_status}_hjelp`, { defaultValue: "" })}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
